import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  TagsField,
  useAsync,
  useNotice,
} from '../../components/common';
import { errorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import {
  billing,
  useBillingWrite,
  type BillingReportInfo,
  type BillingSchedule,
  type PeriodKind,
  type ScheduleInput,
} from '../api/client';
import { EMAIL, reportTitle, targetLabel, targetsOf } from '../shared';
import '../../pages/statistics/statistics.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS: { value: PeriodKind; label: string }[] = [
  { value: 'previousMonth', label: 'Previous month' },
  { value: 'previousWeek', label: 'Previous week (Mon–Sun)' },
];

/** `m h d * *` (monthly) or `m h * * w` (weekly) as the editor's fields; null = something else (edited as raw cron). */
export function parseCron(cron: string): { day: number; time: string; weekly: boolean } | null {
  const m = /^(\d{1,2}) (\d{1,2}) (\*|\d{1,2}) \* (\*|[0-7])$/.exec(cron.trim());
  if (!m || (m[3] === '*') === (m[4] === '*')) return null;
  const time = `${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')}`;
  return m[3] === '*'
    ? { day: Number(m[4]) || 7, time, weekly: true }
    : { day: Number(m[3]), time, weekly: false };
}

export function toCron(day: number, time: string, weekly: boolean): string {
  const [h, m] = time.split(':').map(Number);
  return weekly ? `${m} ${h} * * ${day % 7}` : `${m} ${h} ${day} * *`;
}

export function cronText(cron: string, tz: string): string {
  const p = parseCron(cron);
  if (!p) return `cron ${cron} (${tz})`;
  return p.weekly ? `${DAYS[p.day - 1]} ${p.time} (${tz})` : `day ${p.day} of the month, ${p.time} (${tz})`;
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

export function scheduleProblems(id: string, v: ScheduleInput): string[] {
  const p: string[] = [];
  if (!/^[a-z0-9][a-z0-9-]{1,62}$/.test(id)) p.push('id: 2–63 lowercase letters, digits or -');
  if (!v.name.trim()) p.push('name is required');
  if (!v.recipients.length) p.push('at least one recipient');
  const bad = v.recipients.filter((r) => !EMAIL.test(r));
  if (bad.length) p.push(`not an e-mail address: ${bad.join(', ')}`);
  if (v.recipients.length > 20) p.push('at most 20 recipients');
  if (v.cron.trim().split(/\s+/).length !== 5) p.push('cron needs 5 fields');
  return p;
}

function ScheduleDialog({
  initial,
  reports,
  onSaved,
  onClose,
}: {
  initial: BillingSchedule | null;
  reports: BillingReportInfo[];
  onSaved: (s: BillingSchedule) => void;
  onClose: () => void;
}) {
  const first = reports[0];
  const [id, setId] = useState(initial?.id ?? '');
  const [idTouched, setIdTouched] = useState(!!initial);
  const [v, setV] = useState<ScheduleInput>(() =>
    initial
      ? {
          name: initial.name,
          reportId: initial.reportId,
          target: initial.target,
          periodKind: initial.periodKind,
          cron: initial.cron,
          timezone: initial.timezone,
          recipients: initial.recipients,
          enabled: initial.enabled,
        }
      : {
          name: first ? `${first.title} monthly` : '',
          reportId: first?.id ?? '',
          target: targetsOf(first)[0]?.id ?? '',
          periodKind: 'previousMonth',
          cron: '0 6 1 * *',
          timezone: 'Europe/Stockholm',
          recipients: [],
          enabled: true,
        }
  );
  const [raw, setRaw] = useState(() => !parseCron(v.cron));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof ScheduleInput>(k: K, value: ScheduleInput[K]) =>
    setV((cur) => ({ ...cur, [k]: value }));
  const report = reports.find((r) => r.id === v.reportId);
  const parsed = parseCron(v.cron);
  const weekly = v.periodKind === 'previousWeek';
  const problems = scheduleProblems(id, v);

  const setPeriod = (k: PeriodKind) => {
    // A weekly report runs on Mondays, a monthly one on the 1st; keep the time.
    const time = parsed?.time ?? '06:00';
    setV((cur) => ({ ...cur, periodKind: k, cron: toCron(1, time, k === 'previousWeek') }));
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await billing.saveSchedule(id, { ...v, name: v.name.trim(), cron: v.cron.trim() }));
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? `Schedule ${initial.name}` : 'New billing schedule'}
      maxWidth={720}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={problems.length > 0} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Name"
          value={v.name}
          onChange={(e) => {
            set('name', e.target.value);
            if (!idTouched) setId(slug(e.target.value));
          }}
        />
        <TextField
          label="Id"
          value={id}
          disabled={!!initial}
          onChange={(e) => {
            setIdTouched(true);
            setId(e.target.value);
          }}
          hint="lowercase slug, fixed once saved"
        />
        <Select
          label="Report"
          value={v.reportId}
          onChange={(r) => {
            const next = reports.find((x) => x.id === r);
            setV((cur) => ({
              ...cur,
              reportId: r,
              target: next?.targets.some((t) => t.id === cur.target)
                ? cur.target
                : (targetsOf(next)[0]?.id ?? ''),
            }));
          }}
          options={reports.map((r) => ({ value: r.id, label: r.title }))}
        />
        <Select
          label="Environment"
          value={v.target}
          onChange={(t) => set('target', t)}
          options={targetsOf(report).map((t) => ({
            value: t.id,
            label: t.configured ? t.label : `${t.label} (not configured)`,
          }))}
        />
        <Select
          label="Period"
          value={v.periodKind}
          onChange={(k) => setPeriod(k as PeriodKind)}
          options={PERIODS}
          hint="the period before the run, in the schedule's time zone"
        />
        <TextField
          label="Time zone (IANA)"
          value={v.timezone}
          onChange={(e) => set('timezone', e.target.value)}
        />
        {raw || !parsed ? (
          <TextField
            label="Cron (minute hour day month weekday)"
            value={v.cron}
            onChange={(e) => set('cron', e.target.value)}
            fieldClassName="nyk-span2"
          />
        ) : (
          <>
            {weekly ? (
              <Select
                label="Weekday"
                value={String(parsed.day)}
                onChange={(d) => set('cron', toCron(Number(d), parsed.time, true))}
                options={DAYS.map((d, i) => ({ value: String(i + 1), label: d }))}
              />
            ) : (
              <Select
                label="Day of month"
                value={String(parsed.day)}
                onChange={(d) => set('cron', toCron(Number(d), parsed.time, false))}
                options={Array.from({ length: 28 }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1),
                }))}
              />
            )}
            <TextField
              label="Time"
              type="time"
              value={parsed.time}
              onChange={(e) =>
                e.target.value && set('cron', toCron(parsed.day, e.target.value, parsed.weekly))
              }
            />
          </>
        )}
        <div className="nyk-span2">
          <TagsField
            label="Recipients"
            value={v.recipients}
            onChange={(r) => set('recipients', r)}
            placeholder="name@example.com"
            hint="One mail per run with the PDF and the Excel file attached."
          />
        </div>
        <Switch checked={v.enabled} onChange={(e) => set('enabled', e)} label="Enabled" />
        <Button size="sm" variant="ghost" onClick={() => setRaw((r) => !r)}>
          {raw ? 'Simple schedule' : 'Edit as cron'}
        </Button>
      </div>
      {problems.length > 0 && <p className="nyk-ui-hint">To save: {problems.join(' · ')}</p>}
    </Dialog>
  );
}

function TestMailDialog({ onClose, onSent }: { onClose: () => void; onSent: (to: string[]) => void }) {
  const [to, setTo] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await billing.testMail(to);
      onSent(to);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Send a test mail"
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!to.length || to.some((r) => !EMAIL.test(r))}
            onClick={() => void send()}
          >
            Send
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <TagsField label="Recipients" value={to} onChange={setTo} placeholder="name@example.com" />
    </Dialog>
  );
}

/** Billing schedules: CRUD, run now and a test mail for writers; the list and the last run for readers. */
export function BillingSchedulesPage() {
  const catalog = useAsync(() => billing.catalog(), []);
  const list = useAsync(() => billing.schedules(), []);
  const canWrite = useBillingWrite();
  const [editing, setEditing] = useState<BillingSchedule | 'new' | null>(null);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const toast = useNotice();
  const reports = catalog.data?.reports ?? [];

  const remove = async (s: BillingSchedule) => {
    try {
      await billing.deleteSchedule(s.id);
      toast.success(`Deleted ${s.name}`);
      await list.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const runNow = async (s: BillingSchedule) => {
    setRunning(s.id);
    try {
      const r = await billing.runSchedule(s.id);
      toast.success(`${r.periodLabel} sent to ${r.recipients.join(', ')}`);
      await list.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setRunning(null);
    }
  };
  const items = list.data ?? [];

  return (
    <Page
      title="Billing schedules"
      description="Reports mailed on a schedule. A run covers the period before it (by default the previous month); a failed run is retried every 15 minutes, up to 8 times."
      actions={
        canWrite && (
          <>
            <Button variant="ghost" onClick={() => setTesting(true)}>
              Test mail
            </Button>
            <Button disabled={!reports.length} onClick={() => setEditing('new')}>
              New schedule
            </Button>
          </>
        )
      }
    >
      {toast.element}
      {catalog.data && !catalog.data.mailConfigured && (
        <p className="nyk-notice nyk-notice-warn">
          Mail is not configured (Mail:Host/From): scheduled reports cannot be sent.
        </p>
      )}
      <ErrorBox error={list.error ?? catalog.error} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="billing-schedules">
            <thead>
              <tr>
                <th>Schedule</th>
                <th>Report</th>
                <th>When</th>
                <th>Recipients</th>
                <th>Next run</th>
                <th>Last run</th>
              </tr>
            </thead>
            {items.map((s) => (
              <tbody key={s.id} className="nyk-row-group" data-schedule={s.id}>
                <tr className={s.enabled ? undefined : 'nyk-row-muted'}>
                  <td className="nyk-card-title">
                    {s.name}
                    {!s.enabled && <span className="nyk-ui-hint"> · disabled</span>}
                  </td>
                  <td data-label="Report">
                    {reportTitle(reports, s.reportId)}
                    <div className="nyk-ui-hint">
                      {targetLabel(reports, s.reportId, s.target)} ·{' '}
                      {PERIODS.find((p) => p.value === s.periodKind)?.label}
                    </div>
                  </td>
                  <td data-label="When">{cronText(s.cron, s.timezone)}</td>
                  <td data-label="Recipients" className="nyk-wrap">
                    {s.recipients.join(', ')}
                  </td>
                  <td data-label="Next run">
                    {s.enabled ? formatDateTime(s.nextRunAt) : '—'}
                    {s.retryAt && (
                      <div className="nyk-text-amber">
                        retry {formatDateTime(s.retryAt)} (attempt {s.attempts + 1})
                      </div>
                    )}
                  </td>
                  <td data-label="Last run">
                    {s.lastRunId ? (
                      <Link to={`/billing/reports?run=${s.lastRunId}`}>{formatDateTime(s.lastRunAt)}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                {canWrite && (
                  <ActionsRow colSpan={6}>
                    <ConfirmButton
                      size="sm"
                      variant="ghost"
                      loading={running === s.id}
                      message={`Run "${s.name}" now and mail it to ${s.recipients.join(', ')}?`}
                      onConfirm={() => void runNow(s)}
                    >
                      Run now
                    </ConfirmButton>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                      Edit
                    </Button>
                    <Link
                      className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
                      to={`/billing/runs?schedule=${s.id}`}
                    >
                      Runs
                    </Link>
                    <ConfirmButton
                      size="sm"
                      variant="danger"
                      message={`Delete the schedule "${s.name}"? Its runs stay in the history.`}
                      onConfirm={() => void remove(s)}
                    >
                      Delete
                    </ConfirmButton>
                  </ActionsRow>
                )}
              </tbody>
            ))}
          </table>
        </TableScroller>
        {list.data && !items.length && (
          <EmptyState
            title="No billing schedules."
            hint={canWrite ? 'Create one to mail a report every month.' : undefined}
          />
        )}
      </div>
      {editing && (
        <ScheduleDialog
          initial={editing === 'new' ? null : editing}
          reports={reports}
          onSaved={(s) => {
            toast.success(`Saved ${s.name}`);
            void list.reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {testing && (
        <TestMailDialog
          onClose={() => setTesting(false)}
          onSent={(to) => toast.success(`Test mail sent to ${to.join(', ')}`)}
        />
      )}
    </Page>
  );
}
